import {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import "./projects.css";
import {
  addProject,
  addModuleByEmployee,
  addTaskByEmployee,
  getAllMyProjects,
  getAllProjectsForAdmin,
  getAllModulesForAdmin,
  updateProject,
  deleteProject,
  updateModule,
  deleteModule,
} from "../../service/project_service";
import { toast } from "react-toastify";
import {
  PlusCircle,
  Layers,
  CheckCircle,
  Send,
  RotateCcw,
  Edit,
  Trash2,
  Search,
  X,
  Eye,
  Folder,
  Calendar,
  Clock,
} from "lucide-react";
import { CustomSearchableSelect } from "../../components/admin/AdminFields";
import { getRole } from "../../utils/auth";
import DataTable from "../../components/custom_data_table";

function Projects() {
  const role = getRole();
  const [projectsList, setProjectsList] = useState([]);
  const [modulesList, setModulesList] = useState([]);
  // [myProjectsOptions removed]
  const [loadingProjects, setLoadingProjects] = useState(true);
  const [loadingModules, setLoadingModules] = useState(true);

  // Refs for scrolling
  const projectFormRef = useRef(null);
  const moduleFormRef = useRef(null);

  // Project Form State
  const [projectForm, setProjectForm] = useState({
    projectid: "",
    projectname: "",
    description: "",
    startdate: "",
    enddate: "",
  });
  const [isSubmittingProject, setIsSubmittingProject] = useState(false);
  const [isUpdateProject, setIsUpdateProject] = useState(false);

  // Module Form State
  const [moduleForm, setModuleForm] = useState({
    moduleid: "",
    projectid: "",
    modulename: "",
    description: "",
  });
  const [isSubmittingModule, setIsSubmittingModule] = useState(false);
  const [isUpdateModule, setIsUpdateModule] = useState(false);

  // Task Form State
  const [taskForm, setTaskForm] = useState({
    projectid: 0,
    moduleid: 0,
    taskname: "",
    description: "",
  });
  const [isSubmittingTask, setIsSubmittingTask] = useState(false);

  // Search Terms
  const [projectSearch, setProjectSearch] = useState("");
  const [moduleSearch, setModuleSearch] = useState("");

  // Modal States
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const [showViewModal, setShowViewModal] = useState(false);
  const [viewData, setViewData] = useState(null);
  const [viewType, setViewType] = useState("");

  const fetchData = useCallback(async () => {
    setLoadingProjects(true);
    setLoadingModules(true);
    try {
      const [projRes, modRes] = await Promise.all([
        role === "manager" ? getAllProjectsForAdmin() : getAllMyProjects(),
        getAllModulesForAdmin(),
      ]);

      if (projRes.status === "success" || projRes.status === true) {
        setProjectsList(projRes.data || []);
      }
      if (modRes.status === "success" || modRes.status === true) {
        setModulesList(modRes.data || []);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoadingProjects(false);
      setLoadingModules(false);
    }
  }, [role]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleProjectSubmit = async (e) => {
    e.preventDefault();

    if (!projectForm.projectname?.trim())
      return toast.error("Project name is required");
    if (!projectForm.startdate) return toast.error("Start date is required");
    if (!projectForm.enddate) return toast.error("Estimated Completion date is required");

    if (
      projectForm.enddate &&
      new Date(projectForm.enddate) <= new Date(projectForm.startdate)
    ) {
      return toast.error("End date must be greater than start date");
    }

    setIsSubmittingProject(true);
    try {
      const response = isUpdateProject
        ? await updateProject(projectForm)
        : await addProject(projectForm);
      if (response.status === "success") {
        toast.success(
          isUpdateProject
            ? "Project updated successfully!"
            : "Project created successfully!",
        );
        resetProjectForm();
        fetchData();
      } else {
        toast.error(response.message || "Failed to save project");
      }
    } catch (error) {
      toast.error(error.message || "Something went wrong");
    } finally {
      setIsSubmittingProject(false);
    }
  };

  const resetProjectForm = () => {
    setProjectForm({
      projectid: "",
      projectname: "",
      description: "",
      startdate: "",
      enddate: "",
    });
    setIsUpdateProject(false);
  };

  const handleModuleSubmit = async (e) => {
    e.preventDefault();

    if (!moduleForm.projectid) return toast.error("Please select a project");
    if (!moduleForm.modulename?.trim())
      return toast.error("Module name is required");

    setIsSubmittingModule(true);
    try {
      const response = isUpdateModule
        ? await updateModule(moduleForm)
        : await addModuleByEmployee(moduleForm);
      if (response.status === "success") {
        toast.success(
          isUpdateModule
            ? "Module updated successfully!"
            : "Module created successfully!",
        );
        resetModuleForm();
        fetchData();
      } else {
        toast.error(response.message || "Failed to save module");
      }
    } catch (error) {
      toast.error(error.message || "Something went wrong");
    } finally {
      setIsSubmittingModule(false);
    }
  };
  const resetModuleForm = () => {
    setModuleForm({
      moduleid: "",
      projectid: "",
      modulename: "",
      description: "",
    });
    setIsUpdateModule(false);
  };

  const handleTaskSubmit = async (e) => {
    e.preventDefault();

    if (!taskForm.projectid) return toast.error("Please select a project");
    if (!taskForm.moduleid) return toast.error("Please select a module");
    if (!taskForm.taskname?.trim())
      return toast.error("Task name is required");

    setIsSubmittingTask(true);
    try {
      const response = await addTaskByEmployee(taskForm);
      if (response.status === "success" || response.status === true) {
        toast.success("Task created and assigned successfully!");
        setTaskForm({
          projectid: "",
          moduleid: "",
          taskname: "",
          description: "",
        });
        fetchData();
      } else {
        toast.error(response.message || "Failed to save task");
      }
    } catch (error) {
      toast.error(error.message || "Something went wrong");
    } finally {
      setIsSubmittingTask(false);
    }
  };

  const handleEditProject = (project) => {
    setProjectForm({
      projectid: project.projectid,
      projectname: project.projectname,
      description: project.description,
      startdate: project.startdate?.split("T")[0] || "",
      enddate: project.enddate?.split("T")[0] || "",
    });
    setIsUpdateProject(true);
    projectFormRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const handleEditModule = (module) => {
    setModuleForm({
      moduleid: module.moduleid,
      projectid: module.projectid,
      modulename: module.modulename,
      description: module.description,
    });
    setIsUpdateModule(true);
    moduleFormRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const handleViewDetails = (type, data) => {
    setViewType(type);
    setViewData(data);
    setShowViewModal(true);
  };

  const handleDeleteClick = (type, id, projectId) => {
    setDeleteTarget({ type, id, projectId });
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!deleteTarget || isDeleting) return;
    setIsDeleting(true);
    try {
      let response;
      if (deleteTarget.type === "project") {
        response = await deleteProject(deleteTarget.id);
      } else {
        response = await deleteModule(deleteTarget.id, deleteTarget.projectId);
      }

      if (response.status === "success") {
        toast.success(
          `${deleteTarget.type === "project" ? "Project" : "Module"} deleted successfully`,
        );
        fetchData();
        setShowDeleteModal(false);
        setDeleteTarget(null);
      } else {
        toast.error(response.message || "Failed to delete");
      }
    } catch (error) {
      toast.error(error.message || "Something went wrong");
    } finally {
      setIsDeleting(false);
    }
  };

  const projectColumns = [
    { key: "sno", label: "S.No" },
    {
      key: "actions",
      label: "Actions",
      render: (_, row) => (
        <div className="table-actions">
          <button
            className="action-btn view"
            title="View"
            onClick={() => handleViewDetails("Project", row)}
            style={{ background: "none" }}
          >
            <Eye size={18} color="#2563eb" />
          </button>
          {role === "manager" && (
            <>
              <button
                className="action-btn edit"
                title="Edit"
                onClick={() => handleEditProject(row)}
                style={{ background: "none" }}
              >
                <Edit size={18} color="#059669" />
              </button>
              <button
                className="action-btn delete"
                title="Delete"
                onClick={() => handleDeleteClick("project", row.projectid)}
                style={{ background: "none" }}
              >
                <Trash2 size={18} color="#ef4444" />
              </button>
            </>
          )}
        </div>
      ),
    },
    { key: "projectname", label: "Project Name" },
    {
      key: "description",
      label: "Description",
      render: (val) => <div className="description-text">{val}</div>,
    },
    {
      key: "startdate",
      label: "Start Date",
      render: (val) =>
        val ? new Date(val).toLocaleDateString("en-GB") : "N/A",
    },
    {
      key: "enddate",
      label: "End Date",
      render: (val) =>
        val ? new Date(val).toLocaleDateString("en-GB") : "N/A",
    },
  ];

  const moduleColumns = [
    { key: "sno", label: "S.No" },
    {
      key: "actions",
      label: "Actions",
      render: (_, row) => (
        <div className="table-actions">
          <button
            className="action-btn view"
            title="View"
            onClick={() => handleViewDetails("Module", row)}
            style={{ background: "none" }}
          >
            <Eye size={18} color="#2563eb" />
          </button>
          {role === "manager" && (
            <>
              <button
                className="action-btn edit"
                title="Edit"
                onClick={() => handleEditModule(row)}
                style={{ background: "none" }}
              >
                <Edit size={18} color="#059669" />
              </button>
              <button
                className="action-btn delete"
                title="Delete"
                onClick={() =>
                  handleDeleteClick("module", row.moduleid, row.projectid)
                }
                style={{ background: "none" }}
              >
                <Trash2 size={18} color="#ef4444" />
              </button>
            </>
          )}
        </div>
      ),
    },
    { key: "modulename", label: "Module Name" },
    { key: "projectname", label: "Project" },
    {
      key: "description",
      label: "Description",
      render: (val) => <div className="description-text">{val}</div>,
    },
  ];

  const filteredProjects = projectsList.filter((p) => {
    // Basic Search
    const matchesSearch =
      p.projectname?.toLowerCase().includes(projectSearch.toLowerCase()) ||
      p.description?.toLowerCase().includes(projectSearch.toLowerCase());

    // Strict Filter by Form Selection: If a project is being worked on in the form, ONLY show that project
    if (moduleForm.projectid) {
      return String(p.projectid) === String(moduleForm.projectid);
    }

    return matchesSearch;
  });

  const filteredModules = modulesList.filter((m) => {
    // Role-Based Isolation: Only show modules for projects the user is authorized for
    const projectIds = projectsList.map((p) => String(p.projectid));
    if (!projectIds.includes(String(m.projectid))) {
      return false;
    }

    // Basic Search
    const matchesSearch =
      m.modulename?.toLowerCase().includes(moduleSearch.toLowerCase()) ||
      m.projectname?.toLowerCase().includes(moduleSearch.toLowerCase()) ||
      m.description?.toLowerCase().includes(moduleSearch.toLowerCase());

    // Strict Filter by Form Selection: If a project is being worked on in the form, ONLY show its modules
    if (moduleForm.projectid) {
      return String(m.projectid) === String(moduleForm.projectid);
    }

    return matchesSearch;
  });

  const projectsWithSno = useMemo(
    () => filteredProjects.map((p, i) => ({ ...p, sno: i + 1 })),
    [filteredProjects],
  );

  const modulesWithSno = useMemo(
    () => filteredModules.map((m, i) => ({ ...m, sno: i + 1 })),
    [filteredModules],
  );

  return (
    <div className="projects-view-container">
      <h2>Projects Management</h2>

      <div className="projects-grid-layout">
        <div
          className="project-form-card project-main-card"
          ref={projectFormRef}
        >
          <div className="form-header-with-icon">
            <div className="icon-badge project-icon">
              <PlusCircle size={22} />
            </div>
            <div className="header-text">
              <h5>{isUpdateProject ? "Update Project" : "Add New Project"}</h5>
              <span>
                {isUpdateProject
                  ? "Modify workspace details"
                  : "Initialize core workspace"}
              </span>
            </div>
          </div>
          <form className="crm-form" onSubmit={handleProjectSubmit}>
            <div className="form-group-row">
              <div className="report-form-group">
                <label>Project Identification *</label>
                <input
                  type="text"
                  placeholder="e.g., QPay Mobile App"
                  value={projectForm.projectname}
                  onChange={(e) =>
                    setProjectForm({
                      ...projectForm,
                      projectname: e.target.value,
                    })
                  }
                  required
                />
              </div>
            </div>

            <div className="form-group-row dual-fields">
              <div className="report-form-group">
                <label>Commencement Date *</label>
                <input
                  type="date"
                  value={projectForm.startdate}
                  onChange={(e) =>
                    setProjectForm({
                      ...projectForm,
                      startdate: e.target.value,
                    })
                  }
                  required
                />
              </div>
              <div className="report-form-group">
                <label>Estimated Completion *</label>
                <input
                  type="date"
                  value={projectForm.enddate}
                  onChange={(e) =>
                    setProjectForm({ ...projectForm, enddate: e.target.value })
                  }
                  required
                />
              </div>
            </div>
            <div className="form-group-row">
              <div className="report-form-group">
                <label>Project Overview *</label>
                <textarea
                  placeholder="Briefly describe the project scope and goals..."
                  rows="4"
                  value={projectForm.description}
                  onChange={(e) =>
                    setProjectForm({
                      ...projectForm,
                      description: e.target.value,
                    })
                  }
                  required
                ></textarea>
              </div>
            </div>
            <div className="form-actions-centered">
              <button
                type="button"
                className="crm-btn-secondary"
                disabled={isSubmittingProject}
                onClick={resetProjectForm}
              >
                <RotateCcw size={18} style={{ marginRight: "8px" }} />
                {isUpdateProject ? "Cancel" : "Reset"}
              </button>
              <button
                type="submit"
                className="crm-btn-primary"
                disabled={isSubmittingProject}
              >
                {isSubmittingProject ? (
                  "Processing..."
                ) : (
                  <>
                    <Send size={18} style={{ marginRight: "8px" }} />
                    {isUpdateProject ? "Update" : "Submit"}
                  </>
                )}
              </button>
            </div>
          </form>
        </div>

        {/* Create Project Module Form */}
        <div className="project-form-card module-main-card" ref={moduleFormRef}>
          <div className="form-header-with-icon">
            <div className="icon-badge module-icon">
              <Layers size={22} />
            </div>
            <div className="header-text">
              <h5>{isUpdateModule ? "Update Module" : "Define Module"}</h5>
              <span>
                {isUpdateModule
                  ? "Modify task cluster"
                  : "Break down into task clusters"}
              </span>
            </div>
          </div>
          <form className="crm-form" onSubmit={handleModuleSubmit}>
            <div className="form-group-row">
              <div className="report-form-group">
                <CustomSearchableSelect
                  label="Parent Project *"
                  placeholder={
                    loadingProjects
                      ? "Gathering data..."
                      : "Select parent project"
                  }
                  value={moduleForm.projectid}
                  required={true}
                  onChange={(e) =>
                    setModuleForm({ ...moduleForm, projectid: e.target.value })
                  }
                  options={projectsList.map((project) => ({
                    label: project.projectname,
                    value: project.projectid,
                  }))}
                />
              </div>
            </div>
            <div className="form-group-row" style={{ marginTop: "-20px" }}>
              <div className="report-form-group">
                <label>Module Title *</label>
                <input
                  type="text"
                  placeholder="e.g., API Infrastructure"
                  value={moduleForm.modulename}
                  onChange={(e) =>
                    setModuleForm({ ...moduleForm, modulename: e.target.value })
                  }
                  required
                />
              </div>
            </div>
            <div className="form-group-row">
              <div className="report-form-group">
                <label>Functional Description</label>
                <textarea
                  placeholder="Explain the specific focus of this module..."
                  rows="4"
                  value={moduleForm.description}
                  onChange={(e) =>
                    setModuleForm({
                      ...moduleForm,
                      description: e.target.value,
                    })
                  }
                  
                ></textarea>
              </div>
            </div>
            <div className="form-actions-centered">
              <button
                type="button"
                className="crm-btn-secondary"
                disabled={isSubmittingModule}
                onClick={resetModuleForm}
              >
                <RotateCcw size={18} style={{ marginRight: "8px" }} />
                {isUpdateModule ? "Cancel" : "Reset"}
              </button>
              <button
                type="submit"
                className="crm-btn-primary"
                disabled={isSubmittingModule}
              >
                {isSubmittingModule ? (
                  "Processing..."
                ) : (
                  <>
                    <Layers size={18} style={{ marginRight: "8px" }} />
                    {isUpdateModule ? "Update" : "Submit"}
                  </>
                )}
              </button>
            </div>
          </form>
        </div>

        {/* Create Project Task Form */}
        <div className="project-form-card task-main-card">
          <div className="form-header-with-icon">
            <div className="icon-badge task-icon" style={{ backgroundColor: '#f0fdf4', color: '#16a34a' }}>
              <CheckCircle size={22} />
            </div>
            <div className="header-text">
              <h5>Define Task</h5>
              <span>Create and assign task to yourself</span>
            </div>
          </div>
          <form className="crm-form" onSubmit={handleTaskSubmit}>
            <div className="form-group-row">
              <div className="report-form-group">
                <CustomSearchableSelect
                  label="Parent Project *"
                  placeholder="Select project"
                  value={taskForm.projectid}
                  required={true}
                  onChange={(e) =>
                    setTaskForm({ ...taskForm, projectid: e.target.value, moduleid: "" })
                  }
                  options={projectsList.map((project) => ({
                    label: project.projectname,
                    value: project.projectid,
                  }))}
                />
              </div>
            </div>
            <div className="form-group-row" style={{ marginTop: "-20px" }}>
              <div className="report-form-group">
                <CustomSearchableSelect
                  label="Parent Module *"
                  placeholder="Select module"
                  value={taskForm.moduleid}
                  required={true}
                  onChange={(e) =>
                    setTaskForm({ ...taskForm, moduleid: e.target.value })
                  }
                  options={modulesList
                    .filter(m => String(m.projectid) === String(taskForm.projectid))
                    .map((module) => ({
                      label: module.modulename,
                      value: module.moduleid,
                    }))}
                  disabled={!taskForm.projectid}
                />
              </div>
            </div>
            <div className="form-group-row" style={{ marginTop: "-20px" }}>
              <div className="report-form-group">
                <label>Task Title *</label>
                <input
                  type="text"
                  placeholder="e.g., Design Checkout Flow"
                  value={taskForm.taskname}
                  onChange={(e) =>
                    setTaskForm({ ...taskForm, taskname: e.target.value })
                  }
                  required
                />
              </div>
            </div>
            <div className="form-group-row">
              <div className="report-form-group">
                <label>Task Description</label>
                <textarea
                  placeholder="Describe the task details..."
                  rows="2"
                  value={taskForm.description}
                  onChange={(e) =>
                    setTaskForm({
                      ...taskForm,
                      description: e.target.value,
                    })
                  }
                ></textarea>
              </div>
            </div>
            <div className="form-actions-centered">
              <button
                type="button"
                className="crm-btn-secondary"
                disabled={isSubmittingTask}
                onClick={() => setTaskForm({
                  projectid: "",
                  moduleid: "",
                  taskname: "",
                  description: "",
                })}
              >
                <RotateCcw size={18} style={{ marginRight: "8px" }} />
                Reset
              </button>
              <button
                type="submit"
                className="crm-btn-primary"
                disabled={isSubmittingTask}
                style={{ backgroundColor: '#16a34a' }}
              >
                {isSubmittingTask ? (
                  "Processing..."
                ) : (
                  <>
                    <CheckCircle size={18} style={{ marginRight: "8px" }} />
                    Submit Task
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Project and Module Tables */}
      <div
        className="project-lists-section"
        style={{
          marginTop: "50px",
          display: "flex",
          flexDirection: "column",
          gap: "40px",
        }}
      >
        {/* Projects Table */}
        <div
          className="project-table-card"
          style={{
            background: "#fff",
            borderRadius: "20px",
            padding: "24px",
            border: "1px solid var(--border-light)",
          }}
        >
          <div className="admin-header-section" style={{ marginBottom: "24px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <h4 style={{ margin: 0, display: "flex", alignItems: "center", gap: "10px" }}>
              <PlusCircle size={20} /> All Projects
            </h4>
            <div className="search-wrapper">
              <Search size={18} color="#64748b" />
              <input
                type="text"
                placeholder="Search projects by name or description..."
                value={projectSearch}
                onChange={(e) => setProjectSearch(e.target.value)}
              />
            </div>
          </div>
          <DataTable
            columns={projectColumns}
            data={projectsWithSno}
            loading={loadingProjects}
            pageSize={10}
          />
        </div>

        {/* Modules Table */}
        <div
          className="module-table-card"
          style={{
            background: "#fff",
            borderRadius: "20px",
            padding: "24px",
            border: "1px solid var(--border-light)",
          }}
        >
          <div className="admin-header-section" style={{ marginBottom: "24px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <h4 style={{ margin: 0, display: "flex", alignItems: "center", gap: "10px" }}>
              <Layers size={20} /> All Modules
            </h4>
            <div className="search-controls-group">
              <div className="search-wrapper">
                <Search size={18} color="#64748b" />
                <input
                  type="text"
                  placeholder="Search modules..."
                  value={moduleSearch}
                  onChange={(e) => setModuleSearch(e.target.value)}
                />
              </div>
            </div>
          </div>
          <DataTable
            columns={moduleColumns}
            data={modulesWithSno}
            loading={loadingModules}
            pageSize={10}
          />
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 3000,
            backdropFilter: "blur(4px)",
          }}
        >
          <div
            style={{
              backgroundColor: "#fff",
              padding: "30px",
              borderRadius: "20px",
              maxWidth: "400px",
              width: "90%",
              textAlign: "center",
              boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1)",
            }}
          >
            <div style={{ fontSize: "40px", marginBottom: "15px" }}>🗑️</div>
            <h3 style={{ margin: "0 0 10px 0" }}>Confirm Delete</h3>
            <p
              style={{
                color: "#64748b",
                fontSize: "14px",
                marginBottom: "25px",
              }}
            >
              Are you sure you want to delete this {deleteTarget?.type}? This
              action cannot be undone.
            </p>
            <div
              style={{ display: "flex", gap: "12px", justifyContent: "center" }}
            >
              <button
                onClick={() => !isDeleting && setShowDeleteModal(false)}
                disabled={isDeleting}
                style={{
                  padding: "10px 24px",
                  borderRadius: "10px",
                  border: "1.5px solid #e2e8f0",
                  background: "#fff",
                  fontWeight: "600",
                  cursor: isDeleting ? "not-allowed" : "pointer",
                }}
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                disabled={isDeleting}
                style={{
                  padding: "10px 24px",
                  borderRadius: "10px",
                  border: "none",
                  background: "#ef4444",
                  color: "#fff",
                  fontWeight: "600",
                  cursor: isDeleting ? "not-allowed" : "pointer",
                }}
              >
                {isDeleting ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Detail Modal */}
      {showViewModal && viewData && (
        <div className="premium-dialog-overlay" onClick={() => setShowViewModal(false)}>
          <div className="premium-view-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header-premium">
              <div className="header-title-group">
                <div className="header-icon-badge">
                  <Layers size={20} />
                </div>
                <h5>{viewType} Information Details</h5>
              </div>
              <button className="modal-close-circle" onClick={() => setShowViewModal(false)}>
                <X size={18} />
              </button>
            </div>
            <div className="modal-body-scrollable">
              {viewType === "Project" ? (
                <>
                  <div className="detail-section-card">
                    <div className="detail-row-item">
                      <div className="detail-label-icon">
                        <Folder size={14} />
                        <span>Project Name</span>
                      </div>
                      <div className="detail-value-text">{viewData.projectname}</div>
                    </div>
                    <div className="detail-row-item">
                      <div className="detail-label-icon">
                        <PlusCircle size={14} />
                        <span>Description</span>
                      </div>
                      <div className="remark-surface-box">{viewData.description}</div>
                    </div>
                  </div>

                  <div className="detail-grid-layout">
                    <div className="detail-row-item">
                      <div className="detail-label-icon">
                        <Calendar size={14} />
                        <span>Start Date</span>
                      </div>
                      <div className="detail-value-text">
                        {viewData.startdate ? new Date(viewData.startdate).toLocaleDateString("en-GB") : "N/A"}
                      </div>
                    </div>
                    <div className="detail-row-item">
                      <div className="detail-label-icon">
                        <Clock size={14} />
                        <span>End Date</span>
                      </div>
                      <div className="detail-value-text">
                        {viewData.enddate ? new Date(viewData.enddate).toLocaleDateString("en-GB") : "N/A"}
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className="detail-section-card">
                    <div className="detail-row-item">
                      <div className="detail-label-icon">
                        <Layers size={14} />
                        <span>Module Title</span>
                      </div>
                      <div className="detail-value-text">{viewData.modulename}</div>
                    </div>
                    <div className="detail-row-item">
                      <div className="detail-label-icon">
                        <Folder size={14} />
                        <span>Parent Project</span>
                      </div>
                      <div className="detail-value-text">{viewData.projectname}</div>
                    </div>
                    <div className="detail-row-item">
                      <div className="detail-label-icon">
                        <PlusCircle size={14} />
                        <span>Functional Description</span>
                      </div>
                      <div className="remark-surface-box">{viewData.description}</div>
                    </div>
                  </div>
                </>
              )}
            </div>
            <div className="modal-footer-wrapper">
              <button className="modal-action-btn" onClick={() => setShowViewModal(false)}>
                Dismiss
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Projects;
